const AudioContextCtor = () =>
  typeof window !== "undefined" &&
  (window.AudioContext || window.webkitAudioContext);

export const createCallRingtone = () => {
  let context = null;
  let intervalId = null;
  let activeNodes = [];
  let playing = false;
  let activeMode = null;

  const clearNodes = () => {
    activeNodes.forEach((node) => {
      try {
        if (node.gain) {
          node.gain.cancelScheduledValues(context?.currentTime || 0);
          node.gain.setValueAtTime(0, context?.currentTime || 0);
        }
        node.stop?.();
      } catch {
        // Node may already be stopped.
      }
      try {
        node.disconnect?.();
      } catch {
        // Node may already be disconnected.
      }
    });
    activeNodes = [];
  };

  const playTone = (frequency, offsetSeconds, durationSeconds) => {
    if (!context || !playing) return;

    const oscillator = context.createOscillator();
    const gain = context.createGain();
    const start = context.currentTime + offsetSeconds;
    const end = start + durationSeconds;

    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, start);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(0.14, start + 0.025);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
    activeNodes.push(oscillator, gain);
  };

  const ring = (mode = "incoming") => {
    clearNodes();
    if (!playing || activeMode !== mode) return;
    if (mode === "outgoing") {
      playTone(440, 0, 0.42);
      playTone(554, 0.55, 0.34);
      return;
    }

    playTone(880, 0, 0.28);
    playTone(1175, 0.34, 0.32);
  };

  const start = async (mode = "incoming") => {
    if (playing && activeMode === mode) return;
    playing = true;
    activeMode = mode;

    const Ctor = AudioContextCtor();
    if (!Ctor) {
      playing = false;
      activeMode = null;
      return;
    }

    context ??= new Ctor();
    if (context.state === "suspended") {
      await context.resume().catch(() => {});
    }

    // Check if stop() was invoked while waiting for audio context resume
    if (!playing || activeMode !== mode) {
      clearNodes();
      return;
    }

    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }

    ring(mode);
    intervalId = window.setInterval(() => {
      if (playing && activeMode === mode) {
        ring(mode);
      } else {
        stop();
      }
    }, mode === "outgoing" ? 2200 : 1800);
  };

  const stop = () => {
    playing = false;
    activeMode = null;
    if (intervalId) {
      window.clearInterval(intervalId);
      intervalId = null;
    }
    clearNodes();
  };

  const dispose = () => {
    stop();
    context?.close?.().catch(() => {});
    context = null;
  };

  return {
    start,
    stop,
    dispose,
  };
};

let globalRingtoneInstance = null;

export const getGlobalCallRingtone = () => {
  if (!globalRingtoneInstance) {
    globalRingtoneInstance = createCallRingtone();
  }
  return globalRingtoneInstance;
};
